import { useState, useEffect, useCallback, useMemo } from 'react';
import { PlusIcon, CpuChipIcon } from '@heroicons/react/24/outline';
import { useApi, type Model, type CameraRef, type PageMeta } from '../api';
import { usePermissions } from '../auth';
import ModelList from '../components/models/ModelList';
import { isModelActive } from '../constants/modelSurfaces';
import ModelAttachModal from '../components/models/ModelAttachModal';
import ConfirmDialog from '../components/common/ConfirmDialog';
import Pagination from '../components/common/Pagination';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { PageTitle } from '../components/layout/TitleBar';
import { BTN_PRIMARY, BTN_PRIMARY_SM, BTN_SECONDARY, CARD, INPUT, SECTION_TITLE } from '../components/common/ui';
import {
  FilterColumn, FilterSection, FilterChips, FilterSearch, PageWithFilters, useFilterColumn,
} from '../components/layout/FilterColumn';

export interface ModelsPageProps {
  /** Permission keys differ per node (cloud `ai-models.*`, local `models.*`). */
  permissions?: { add?: string; delete?: string; attach?: string };
  /** Cloud: require a selected node (shows empty state when none). */
  requireNode?: boolean;
  /** Cloud: selected node id — changing it refetches. */
  nodeKey?: string | null;
  /** Extra classes on the page body (the PageWithFilters). */
  className?: string;
}

const DEFAULT_PERMS = { add: 'models.create', delete: 'models.delete', attach: 'models.attach' };

/** Cards per page (client-side — the whole registry is held in memory so filters cover it). */
const PAGE_SIZE = 20;

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];
/** How much of the registry we pull in one go. Registries are small; see the note below. */
const FETCH_LIMIT = 500;

type StatusFilter = 'all' | 'active' | 'inactive';

/**
 * Analytics model management — shared by both dashboards. Models and the attach-camera
 * list both come through useApi().models (cloud's adapter binds the selected node).
 * Permission keys and node lifecycle are injected via props.
 */
export default function ModelsPage({ permissions, requireNode = false, nodeKey, className = '' }: ModelsPageProps) {
  const perms = { ...DEFAULT_PERMS, ...permissions };
  const { models: modelsApi } = useApi();
  const { hasPermission } = usePermissions();
  const [models, setModels] = useState<Model[]>([]);
  const [meta, setMeta] = useState<PageMeta | null>(null);
  const [cameras, setCameras] = useState<CameraRef[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [attachModel, setAttachModel] = useState<Model | null>(null);
  const [deleteModel, setDeleteModel] = useState<Model | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [addForm, setAddForm] = useState({ name: '', description: '' });
  const [addLoading, setAddLoading] = useState(false);

  const noNode = requireNode && !nodeKey;
  const filterColumn = useFilterColumn('model-registry');

  // The registry is fetched whole (not page-by-page) so that search / status filtering and
  // paging all operate on the full set — a filter that only searched the visible page
  // would quietly miss matches.
  const load = useCallback(async () => {
    if (noNode) { setModels([]); setMeta(null); setLoading(false); return; }
    setLoading(true);
    try {
      const res = await modelsApi.list({ page: 1, limit: FETCH_LIMIT });
      setModels(Array.isArray(res.data) ? res.data : []);
      setMeta(res.meta ?? null);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [modelsApi, noNode]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeKey]);

  useEffect(() => {
    if (noNode) { setCameras([]); return; }
    modelsApi.listCameras().then((c) => setCameras(Array.isArray(c) ? c : [])).catch(() => setCameras([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeKey]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return models.filter((m) => {
      if (status !== 'all' && isModelActive(m) !== (status === 'active')) return false;
      if (!q) return true;
      return (m.name || '').toLowerCase().includes(q) || (m.description || '').toLowerCase().includes(q);
    });
  }, [models, search, status]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageModels = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageMeta: PageMeta = { page: Math.min(page, totalPages), totalPages, total: filtered.length };

  // Any filter change starts the reader back at the first page.
  useEffect(() => { setPage(1); }, [search, status]);
  // Keep the page valid when the list shrinks beneath us (e.g. after a delete).
  useEffect(() => { setPage((p) => Math.min(Math.max(1, p), totalPages)); }, [totalPages]);

  const filtersActive = !!search || status !== 'all';
  const clearFilters = () => { setSearch(''); setStatus('all'); };

  // Registries are small, but say so rather than silently showing a subset.
  const serverTotal = meta && typeof meta.total === 'number' ? meta.total : null;
  const truncated = serverTotal !== null && serverTotal > models.length;

  function openAdd() {
    setEditingId(null);
    setAddForm({ name: '', description: '' });
    setShowAdd(true);
  }

  function openEdit(model: Model) {
    setEditingId(model.id);
    setAddForm({ name: model.name, description: model.description || '' });
    setShowAdd(true);
  }

  function closeForm() {
    setShowAdd(false);
    setEditingId(null);
    setAddForm({ name: '', description: '' });
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setAddLoading(true);
    try {
      if (editingId) await modelsApi.update(editingId, addForm);
      else await modelsApi.create(addForm);
      closeForm();
      load();
    } catch {
      /* toast */
    } finally {
      setAddLoading(false);
    }
  }

  async function handleAttach(modelId: string, cameraId: string, config: unknown) {
    await modelsApi.attachToCamera(modelId, cameraId, config);
    load();
  }

  async function handleDelete() {
    if (!deleteModel) return;
    await modelsApi.delete(deleteModel.id);
    setDeleteModel(null);
    load();
  }

  if (noNode) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <CpuChipIcon className="h-12 w-12 mb-4 opacity-40" />
        <p className="text-[15px] font-medium">No site selected</p>
        <p className="mt-1 text-[13px]">Select a site from the title bar to manage analytics models.</p>
      </div>
    );
  }

  return (
    <>
      <PageTitle title="Model Registry">
        {hasPermission(perms.add) && (
          <button data-tour="models-add" onClick={() => (showAdd ? closeForm() : openAdd())} className={BTN_PRIMARY_SM}>
            <PlusIcon className="h-4 w-4" />
            Add Model
          </button>
        )}
      </PageTitle>

      {/* Flush, as on Search & Track: the PageWithFilters is the page body (a direct child of the
          shell's <main>), so nothing may wrap it. The model cards sit on the page background. */}
      <PageWithFilters
        className={className}
        filters={(
          <FilterColumn
            width="wide"
            open={filterColumn.open}
            onToggle={filterColumn.toggle}
            label="Filters"
            activeCount={(search ? 1 : 0) + (status !== 'all' ? 1 : 0)}
            onClearAll={clearFilters}
          >
            <FilterSearch value={search} onChange={setSearch} placeholder="Search name or description…" />
            <FilterSection title="Status">
              <div data-tour="models-status">
                <FilterChips<StatusFilter> value={status} onChange={setStatus} options={STATUS_OPTIONS} />
              </div>
            </FilterSection>
          </FilterColumn>
        )}
      >
        {showAdd && (
          <form onSubmit={handleSubmit} className={`${CARD} p-4`}>
            <h3 className={`mb-4 ${SECTION_TITLE}`}>{editingId ? 'Edit Model' : 'Register New Model'}</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input required value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="Model name" className={INPUT} />
              <input value={addForm.description} onChange={(e) => setAddForm({ ...addForm, description: e.target.value })} placeholder="Description (optional)" className={INPUT} />
            </div>
            <div className="mt-4 flex gap-3">
              <button type="submit" disabled={addLoading} className={BTN_PRIMARY}>{addLoading ? 'Saving…' : editingId ? 'Save Changes' : 'Register Model'}</button>
              <button type="button" onClick={closeForm} className={BTN_SECONDARY}>Cancel</button>
            </div>
          </form>
        )}

        <p className="text-[12px] text-muted-foreground">
          Showing {filtered.length} of {models.length} model{models.length !== 1 ? 's' : ''}
          {truncated && ` · first ${models.length} of ${serverTotal} loaded`}
        </p>

        {loading ? (
          <LoadingSpinner />
        ) : filtered.length === 0 && models.length > 0 ? (
          <div className="py-16 text-center text-muted-foreground">
            <p className="text-[15px] font-medium">No models match these filters</p>
            <button onClick={clearFilters} className="mt-2 text-[13px] text-brand hover:underline">Clear filters</button>
          </div>
        ) : (
          <ModelList
            models={pageModels}
            onAttach={hasPermission(perms.attach) ? (m) => setAttachModel(m) : undefined}
            onEdit={hasPermission(perms.add) ? openEdit : undefined}
            onDelete={hasPermission(perms.delete) ? (m) => setDeleteModel(m) : undefined}
          />
        )}

        <Pagination meta={pageMeta} onPageChange={setPage} />
      </PageWithFilters>

      <ModelAttachModal model={attachModel} cameras={cameras} onAttach={handleAttach} onClose={() => setAttachModel(null)} />

      <ConfirmDialog
        open={!!deleteModel}
        title="Delete Model"
        message={`Are you sure you want to delete "${deleteModel?.name}"? All camera attachments will be removed.`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteModel(null)}
      />
    </>
  );
}
